import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Radar, X, ArrowRight } from 'lucide-react';
import { useAutopilot } from '@/features/autopilot/AutopilotContext';

const money = (n) => `$${Math.round(n).toLocaleString()}`;
const SEV = { high: 'text-rose-600', medium: 'text-amber-600', low: 'text-slate-500' };

// Tailwind's `lg` breakpoint. The guide's ONLY in-app on/off control lives in
// AutopilotMenu, in the header cluster, which is `hidden lg:flex` — so below
// `lg` there is no way to reach it. (It used to be a Switch in the sidebar
// rail, also `hidden lg:flex`; the control moved, the constraint did not.) The guide card must therefore be
// desktop-only too: otherwise enabling it on desktop and then shrinking to a
// <lg viewport would strand the card on screen with no in-app off-switch (only
// clearing localStorage could dismiss it). The persisted preference is left
// untouched, so the guide resumes once the viewport is wide enough to control it.
const DESKTOP_QUERY = '(min-width: 1024px)';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    try { return window.matchMedia(DESKTOP_QUERY).matches; } catch { return true; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(DESKTOP_QUERY); } catch { return undefined; }
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches); // sync in case it changed between render and effect
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // Safari < 14 fallback
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isDesktop;
}

// The guided overlay — a non-invasive floating card pointing at the highest-impact
// open recommendation. Never moves the OS cursor, never auto-clicks; "Review fix"
// routes to the Decision Canvas where the human approves. Hidden on the Autopilot
// pages themselves (the feed is already on screen there), and below `lg` where its
// on/off toggle is unreachable.
export default function AutopilotCursor() {
  const { enabled, top, dismiss } = useAutopilot();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  if (!isDesktop || !enabled || !top || location.pathname.startsWith('/autopilot')) return null;
  const v = top.estimatedImpact?.valueAtRisk;
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-card rounded-2xl ring-1 ring-slate-200 shadow-xl p-4">
      <div className="flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0"><Radar className="w-4 h-4 text-indigo-600" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500">Autopilot · top risk</div>
          <div className="text-[13px] font-semibold text-slate-900 leading-snug">{top.title}</div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            {v != null ? `${money(v)} at risk` : 'value unpriced'} · <span className={`capitalize ${SEV[top.severity] || ''}`}>{top.severity}</span>
            {top.feasibilityScore != null ? ` · feasibility ${top.feasibilityScore}` : ''}
          </div>
        </div>
        <button type="button" onClick={() => dismiss(top.id)} aria-label="Dismiss" className="text-slate-300 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      <button type="button" onClick={() => navigate(`/autopilot/canvas/${top.id}`)}
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium py-2 hover:bg-primary/90 transition-colors">
        Review fix <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
